"use client";

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Lock, Sparkles, ArrowRight, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  num: "1",
  title: "Property Details"
}, {
  num: "2",
  title: "Window Breakdown"
}, {
  num: "3",
  title: "Choose Service"
}, {
  num: "4",
  title: "Contact Info"
}];
const serviceTypes = [{
  value: "exterior",
  label: "Exterior",
  description: "Exterior window cleaning",
  icon: Sparkles
}, {
  value: "interior",
  label: "Interior",
  description: "Interior window cleaning",
  icon: Sparkles
}, {
  value: "both",
  label: "Exterior + Interior",
  description: "Full exterior and interior window cleaning",
  icon: Sparkles
}];
const addOns = [{
  id: "screens",
  label: "Screen cleaning",
  price: 15
}, {
  id: "frames",
  label: "Frame detailing",
  price: 20
}, {
  id: "hardwater",
  label: "Hard water removal",
  price: 50
}, {
  id: "interior",
  label: "Interior glass",
  price: 45
}];
const bedroomOptions = ["20-30", "30-40", "40-50", "50+"];
const bathroomOptions = ["1", "2", "3"];
const pricingRanges = {
  '1': {
    min: 100,
    max: 160
  },
  '2': {
    min: 140,
    max: 240
  },
  '3': {
    min: 170,
    max: 280
  }
};
function getPriceByWindowAndStories(windowCountIndex: number, stories: '1' | '2' | '3') {
  const range = pricingRanges[stories];
  const basePrice = range.min + windowCountIndex / 3 * (range.max - range.min);
  return Math.min(basePrice, range.max);
}
function BookingForm() {
  const navigate = useNavigate();
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control,
    watch
  } = useWvcForm();
  const [bedrooms, setBedrooms] = useState("20-30");
  const [bathrooms, setBathrooms] = useState("1");
  const [selectedAddOns, setSelectedAddOns] = useState<string[]>([]);
  const smallCount = parseInt(watch("small_windows") || "0");
  const mediumCount = parseInt(watch("medium_windows") || "0");
  const bigCount = parseInt(watch("big_windows") || "0");
  const basementCount = parseInt(watch("basement_windows") || "0");
  const totalWindows = smallCount + mediumCount + bigCount + basementCount;
  const addonsTotal = addOns.filter(a => selectedAddOns.includes(a.id)).reduce((sum, a) => sum + a.price, 0);
  const breakdownTotal = smallCount * 5 + mediumCount * 10 + bigCount * 15 + basementCount * 8;
  const cleaning_type = watch('cleaning_type');
  const windowCountIndex = bedroomOptions.indexOf(bedrooms);
  const basePrice = getPriceByWindowAndStories(windowCountIndex, bathrooms as '1' | '2' | '3');
  const total = Math.round((cleaning_type === 'both' ? basePrice * 2 - 20 : basePrice) + breakdownTotal + addonsTotal);
  useEffect(() => {
    if (isSubmitted && !submitError) {
      const query = new URLSearchParams({
        total: total.toString(),
        windowCount: totalWindows.toString(),
        cleaning_type: cleaning_type || "",
        selectedAddOns: selectedAddOns.join(',')
      }).toString();
      navigate(`/QuoteConfirmation?${query}`);
    }
  }, [isSubmitted, submitError, navigate, total, totalWindows, cleaning_type, selectedAddOns]);
  if (isSubmitted && !submitError) {
    return <div className="bg-white rounded-lg p-8 md:p-12 shadow-md border border-slate-200">
        <div className="flex flex-col items-center text-center gap-4 py-12">
          <div className="size-16 rounded-lg bg-slate-100 flex items-center justify-center">
            <Check className="size-8 text-slate-900" />
          </div>
          <h3 className="font-default text-3xl text-slate-900">Quote Request Received</h3>
          <p className="text-slate-600 max-w-md">{successMessage}</p>
          <Button onClick={resetForm} variant="outline" className="mt-4 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white">
            Request another quote
          </Button>
        </div>
      </div>;
  }
  return <div className="bg-white rounded-lg p-6 md:p-12 shadow-md border border-slate-200">
      <Form onSubmit={handleSubmit} className="space-y-10">
        <div className="space-y-6">
          <p className="text-xs font-default tracking-widest text-slate-500 uppercase">
            Step 1 — Property Details
          </p>

          <div className="space-y-3">
            <label className="text-sm font-medium text-slate-900 block">Window Count Estimate</label>
            <ToggleGroup type="single" value={bedrooms} onValueChange={v => v && setBedrooms(v)} className="flex flex-wrap gap-2 w-full">
              {bedroomOptions.map((opt, i) => <ToggleGroupItem data-index={i} key={opt} value={opt} className="rounded-md border border-slate-300 bg-white px-5 py-2 text-slate-900 data-[state=on]:bg-slate-900 data-[state=on]:text-white data-[state=on]:border-slate-900 hover:bg-slate-50 hover:text-black transition-colors">
                  {opt}
                </ToggleGroupItem>)}
            </ToggleGroup>
          </div>

          <div className="space-y-3">
            <label className="text-sm font-medium text-slate-900 block">Stories</label>
            <ToggleGroup type="single" value={bathrooms} onValueChange={v => v && setBathrooms(v)} className="flex flex-wrap gap-2 w-full">
              {bathroomOptions.map((opt, i) => <ToggleGroupItem data-index={i} key={opt} value={opt} className="rounded-md border border-slate-300 bg-white px-5 py-2 text-slate-900 data-[state=on]:bg-slate-900 data-[state=on]:text-white data-[state=on]:border-slate-900 hover:bg-slate-50 hover:text-black transition-colors">
                  {opt}
                </ToggleGroupItem>)}
            </ToggleGroup>
          </div>
        </div>

        <Separator />

        <div className="space-y-6">
          <p className="text-xs font-default tracking-widest text-slate-500 uppercase">
            Step 2 — Window Type Breakdown
          </p>
          <p className="text-sm text-slate-600">
            This is just a rough estimate — your numbers don't need to be exact. We'll confirm the exact count when we visit your home.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField control={control} name="small_windows" render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-sm font-medium">Small Windows</FormLabel>
                  <FormControl>
                    <Input type="number" min="0" {...field} className="bg-white border-slate-300 focus-visible:border-slate-900 focus-visible:ring-0" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
            <FormField control={control} name="medium_windows" render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-sm font-medium">Medium Windows</FormLabel>
                  <FormControl>
                    <Input type="number" min="0" {...field} className="bg-white border-slate-300 focus-visible:border-slate-900 focus-visible:ring-0" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
            <FormField control={control} name="big_windows" render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-sm font-medium">Large/Big Windows</FormLabel>
                  <FormControl>
                    <Input type="number" min="0" {...field} className="bg-white border-slate-300 focus-visible:border-slate-900 focus-visible:ring-0" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
            <FormField control={control} name="basement_windows" render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-sm font-medium">Basement Windows</FormLabel>
                  <FormControl>
                    <Input type="number" min="0" {...field} className="bg-white border-slate-300 focus-visible:border-slate-900 focus-visible:ring-0" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>

          <div className="p-4 bg-slate-50 rounded-md border border-slate-200 flex justify-between items-center">
            <span className="text-sm font-medium text-slate-600">Total Breakdown Count:</span>
            <span className="text-lg font-bold text-slate-900">{totalWindows} Windows</span>
          </div>
        </div>

        <Separator />

        <div className="space-y-6">
          <p className="text-xs font-default tracking-widest text-slate-500 uppercase">
            Step 3 — Service Selection
          </p>

          <FormField control={control} name="cleaning_type" rules={{
          required: "Please select a service type"
        }} render={({
          field
        }) => <FormItem>
                <FormControl>
                  <RadioGroup onValueChange={field.onChange} value={field.value} className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {serviceTypes.map((service, i) => {
                const Icon = service.icon;
                const isSelected = field.value === service.value;
                return <label data-index={i} key={service.value} htmlFor={`service-${service.value}`} className={`relative cursor-pointer rounded-md border p-5 transition-all ${isSelected ? "border-slate-900 bg-slate-50 ring-1 ring-slate-900" : "border-slate-200 bg-white hover:bg-slate-50"}`}>
                          <RadioGroupItem value={service.value} id={`service-${service.value}`} className="sr-only" />
                          <div className="flex flex-col gap-2">
                            <Icon className="size-6 text-slate-900" />
                            <div className="font-default font-semibold text-slate-900">
                              {service.label}
                            </div>
                            <div className="text-xs text-slate-600">
                              {service.description}
                            </div>
                          </div>
                        </label>;
              })}
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <Separator />

        <div className="space-y-6">
          <p className="text-xs font-default tracking-widest text-slate-500 uppercase">
            Step 4 — Contact Information
          </p>

          <FormField control={control} name="full_name" rules={{
          required: "Name is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-sm font-medium">Full Name</FormLabel>
                <FormControl>
                  <Input placeholder="John Doe" {...field} className="bg-white text-slate-900 border-slate-300 focus-visible:border-slate-900 focus-visible:ring-0" />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="email" rules={{
          required: "Email is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-sm font-medium">Home Address</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="john@example.com" {...field} className="bg-white text-slate-900 border-slate-300 focus-visible:border-slate-900 focus-visible:ring-0" />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="phone" rules={{
          required: "Phone is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-sm font-medium">Phone Number</FormLabel>
                <FormControl>
                  <Input type="tel" placeholder="289-395-1652" {...field} className="bg-white text-slate-900 border-slate-300 focus-visible:border-slate-900 focus-visible:ring-0" />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-600 text-sm">{submitError}</p>
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-base bg-slate-900 text-white hover:bg-slate-800 rounded-md shadow-md">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Processing...
            </> : <>
              Request Free Quote
              <ArrowRight className="ml-2 size-5" />
            </>}
        </Button>
      </Form>
    </div>;
}
export default function Quote() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="853" id="interactivequotecalculator" className="relative bg-slate-50 py-20 md:py-28 overflow-hidden">
      <div ref={ref} className={`container relative mx-auto px-4 md:px-8 max-w-7xl transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5 space-y-10">
            <div className="space-y-6">
              <div className="flex items-center gap-2">
                <div className="h-px w-8 bg-slate-900" />
                <p className="text-xs font-default tracking-widest text-slate-900 uppercase">
                  Get a Quote
                </p>
              </div>

              <h2 className="font-default text-4xl md:text-5xl lg:text-[48px] leading-[1.1] text-slate-900">
                Luxury Window Cleaning <br />
                <span className="text-slate-500">Guaranteed.</span>
              </h2>

              <p className="text-base md:text-lg text-slate-600 leading-relaxed max-w-md">
                Experience the clarity of professional window cleaning. We provide 
                affordable, high-end service for homes and businesses across the GTA.
              </p>
            </div>

            <div className="space-y-4">
              {steps.map((step, i) => {
              const isActive = i === 0;
              return <div data-index={i} key={step.num} className="flex items-center gap-4">
                    <div className={`size-10 rounded-md flex items-center justify-center font-default text-lg shrink-0 transition-colors ${isActive ? "bg-slate-900 text-white" : "bg-slate-200 text-slate-600"}`}>
                      {step.num}
                    </div>
                    <p className={`text-base ${isActive ? "text-slate-900 font-medium" : "text-slate-600"}`}>
                      {step.title}
                    </p>
                  </div>;
            })}
            </div>

            <div className="bg-white rounded-md p-5 flex items-start gap-3 border border-slate-200">
              <div className="size-10 rounded-md bg-slate-100 flex items-center justify-center shrink-0">
                <Lock className="size-5 text-slate-900" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-900 mb-1">
                  Fast Response
                </p>
                <p className="text-xs text-slate-600 leading-relaxed">
                  We respond to all quote requests within 6 hours.
                </p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <FormProvider formKey="free_quote_form_home" formId="free_quote_form_home" sectionName="interactivequotecalculator" defaultValues={{
            full_name: "",
            email: "",
            phone: "",
            cleaning_type: "",
            small_windows: "0",
            medium_windows: "0",
            big_windows: "0",
            basement_windows: "0"
          }} validationRules={{
            full_name: {
              required: true
            },
            email: {
              required: true
            },
            phone: {
              required: true
            },
            cleaning_type: {
              required: true
            },
            small_windows: {
              min: 0
            },
            medium_windows: {
              min: 0
            },
            big_windows: {
              min: 0
            },
            basement_windows: {
              min: 0
            }
          }} submitText="Request Free Quote" successMessage="We have received your request and will contact you within 6 hours." errorMessage="Something went wrong. Please try again.">
              <BookingForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}